import { useState } from 'react'
import { Loader2, Sparkles } from 'lucide-react'

import { Input } from 'components/ui/input'
import { draftSlide, type SlideDraftBasis, type SlideDraftTarget } from 'services/aiService'

import type { Slide, Story } from '../../types/presentation.types'

const LABELS: Record<SlideDraftTarget, { button: string; again: string }> = {
	notes: { button: 'Draft notes', again: 'Redraft notes' },
	text: { button: 'Draft text', again: 'Redraft text' },
}

/** "from 3 blocks on this slide · 1 not available to you" */
export const basedOnText = ({ blocks, unavailable }: SlideDraftBasis): string => {
	const parts = [blocks > 0 ? `from ${blocks} block${blocks === 1 ? '' : 's'} on this slide` : 'from the slide’s title']
	if (unavailable > 0) parts.push(`${unavailable} not available to you`)
	return parts.join(' · ')
}

const messageOf = (error: any): string => error?.response?.data?.message || "Ask MDT couldn't write a draft just now. Try again in a moment."

/**
 * "Draft notes" / "Draft text" in the deck editor's inspector (MYD-640).
 *
 * Sends the slide as it is in the editor, unsaved edits included; the server
 * resolves its blocks as the user and writes from what they show. The answer
 * is handed to `onApply`, which makes it an ordinary editor edit of its own
 * undo step, unsaved until Save. So "don't like it" is Undo.
 *
 * After a draft, a one-line instruction ("shorter", "lead with traffic") asks
 * for another go.
 */
export function SlideDraftControl({
	presentationId,
	target,
	slide,
	story,
	blockId,
	onApply,
	draft = draftSlide,
}: {
	presentationId: string
	target: SlideDraftTarget
	slide: Slide
	story: Story | null
	/** text: the generic.text block to write */
	blockId?: string
	onApply: (text: string) => void
	draft?: typeof draftSlide
}) {
	const labels = LABELS[target]
	const [busy, setBusy] = useState(false)
	const [error, setError] = useState<string | null>(null)
	const [basis, setBasis] = useState<SlideDraftBasis | null>(null)
	const [instruction, setInstruction] = useState('')

	const write = async () => {
		setBusy(true)
		setError(null)
		try {
			const result = await draft(presentationId, {
				target,
				slide: { title: slide.title, subtitle: slide.subtitle, speakerNotes: slide.speakerNotes, blocks: slide.blocks },
				anchor: story?.anchor ?? null,
				storyTitle: story?.title ?? '',
				...(blockId && { blockId }),
				...(instruction.trim() && { instruction: instruction.trim() }),
			})
			onApply(result.text)
			setBasis(result.basedOn)
			setInstruction('')
		} catch (failure) {
			setError(messageOf(failure))
		} finally {
			setBusy(false)
		}
	}

	return (
		<div className="space-y-1.5">
			<div className="flex items-center gap-2">
				{basis && (
					<Input
						aria-label="What to change"
						value={instruction}
						maxLength={200}
						placeholder="Change it: e.g. shorter"
						onChange={event => setInstruction(event.target.value)}
						onKeyDown={event => {
							if (event.key === 'Enter' && !busy) {
								event.preventDefault()
								write()
							}
						}}
						className="h-7 min-w-0 flex-1 text-xs"
					/>
				)}
				<button
					type="button"
					onClick={write}
					disabled={busy}
					className="inline-flex shrink-0 items-center gap-1 text-xs font-medium text-primary hover:underline disabled:opacity-50"
				>
					{busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" /> : <Sparkles className="h-3.5 w-3.5" aria-hidden="true" />}
					{busy ? 'Drafting…' : basis ? labels.again : labels.button}
				</button>
			</div>
			{basis && !error && (
				<p className="text-[11px] leading-snug text-muted-foreground" role="status">
					AI draft {basedOnText(basis)}. Check it before you present; Undo puts back what was there.
				</p>
			)}
			{error && (
				<p role="alert" className="text-[11px] text-destructive">
					{error}
				</p>
			)}
		</div>
	)
}

export default SlideDraftControl
